import { useState, useRef, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const INTEGER_REGEX = /^-?\d+$/;
const PARTIAL_REGEX = /^-?\d*$/;
const SEPARATORS = /[\s,;]+/;
const HAS_SEPARATOR = /[\s,;]/;
const INVALID_MESSAGE = 'Only integers are allowed';

// Numeric, not lexicographic: the default comparator would order 10 before 9.
const ASCENDING = (a, b) => a - b;

const isAscending = (values) =>
  values.every((value, index) => index === 0 || values[index - 1] <= value);

// Copy that also works on non-secure origins (navigator.clipboard is undefined on http).
function copyText(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    return;
  }
  fallbackCopy(text);
}

function fallbackCopy(text) {
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.focus();
  el.select();
  try {
    document.execCommand('copy');
  } catch {
    /* ignore */
  }
  document.body.removeChild(el);
}

export function RHFNumberChips({ name, label, helperText }) {
  const { control, setValue } = useFormContext();
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [insertIndex, setInsertIndex] = useState(null); // null = caret at end
  const [focused, setFocused] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState([]); // selected chip indices (for copy)
  const anchorRef = useRef(null); // anchor for shift-range / drag selection
  const draggingRef = useRef(false); // mouse drag-select in progress
  const inputRef = useRef(null);

  useEffect(() => {
    const stop = () => {
      draggingRef.current = false;
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  // Chips are always smallest-first, whatever order they arrive in — typed, pasted, or loaded from
  // a saved record. Sorting the form value rather than only the display keeps every index (delete,
  // caret, selection) pointing at the number the user is looking at.
  const watched = useWatch({ control, name });
  const watchedSignature = Array.isArray(watched) ? watched.join(',') : '';

  useEffect(() => {
    const values = Array.isArray(watched) ? watched : [];

    if (values.length > 1 && !isAscending(values)) {
      // No `shouldDirty`: re-ordering what was loaded isn't an edit the user made.
      setValue(name, [...values].sort(ASCENDING), { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSignature, name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const values = Array.isArray(field.value) ? field.value : [];
        const insertIdx = insertIndex ?? values.length;
        const errorMessage = error?.message || inputError;
        const isError = !!error || !!inputError;
        const shrink = focused || values.length > 0 || inputValue.length > 0;

        const clearSelection = () => {
          if (selected.length) setSelected([]);
        };

        // Sorted on the way in, so the caret no longer decides where a number lands — it decides
        // where the input sits and which neighbour Backspace/Delete takes. After an insert it goes
        // back to the end, which is the one predictable place once order is automatic.
        const insertNumbers = (nums) => {
          if (!nums.length) return;
          const next = [...values, ...nums].sort(ASCENDING);
          setValue(name, next, { shouldValidate: true });
          setInsertIndex(null);
          clearSelection();
        };

        const commit = (raw) => {
          const trimmed = raw.trim();
          if (!trimmed) {
            setInputError('');
            return;
          }
          if (!INTEGER_REGEX.test(trimmed)) {
            setInputError(INVALID_MESSAGE);
            return;
          }
          insertNumbers([Number(trimmed)]);
          setInputValue('');
          setInputError('');
        };

        const deleteAt = (i) => {
          if (i < 0 || i >= values.length) return;
          const next = values.filter((_, idx) => idx !== i);
          setValue(name, next, { shouldValidate: true });
          clearSelection();
          if (insertIndex !== null) {
            if (i < insertIndex) setInsertIndex((prev) => Math.max(0, prev - 1));
            else if (insertIndex >= next.length) setInsertIndex(null);
          }
        };

        const deleteSelected = () => {
          const set = new Set(selected);
          const next = values.filter((_, i) => !set.has(i));
          setValue(name, next, { shouldValidate: true });
          setSelected([]);
          setInsertIndex(null);
        };

        // Typing AND pasting funnel through here: any separator splits off the
        // complete numbers; the trailing partial stays in the input.
        const handleChange = (e) => {
          clearSelection();
          const raw = e.target.value;
          if (!HAS_SEPARATOR.test(raw)) {
            setInputValue(raw);
            setInputError(raw === '' || PARTIAL_REGEX.test(raw) ? '' : INVALID_MESSAGE);
            return;
          }
          const parts = raw.split(SEPARATORS);
          const tail = parts.pop() ?? '';
          const valid = [];
          let invalid = false;
          parts.forEach((p) => {
            if (p === '') return;
            if (INTEGER_REGEX.test(p)) valid.push(Number(p));
            else invalid = true;
          });
          insertNumbers(valid);
          setInputValue(tail);
          setInputError(
            invalid || (tail !== '' && !PARTIAL_REGEX.test(tail)) ? INVALID_MESSAGE : ''
          );
        };

        // Paste commits every number, including the last one (no trailing
        // separator needed) — "1,2,3,4,5" becomes 5 chips.
        const handlePaste = (e) => {
          const text = e.clipboardData?.getData('text') ?? '';
          if (!text.trim()) return;
          e.preventDefault();
          const nums = text
            .split(SEPARATORS)
            .filter((s) => s !== '' && INTEGER_REGEX.test(s))
            .map(Number);
          insertNumbers(nums);
          setInputValue('');
          setInputError('');
        };

        const handleCopy = () => {
          const list = selected.length
            ? selected.slice().sort((a, b) => a - b).map((i) => values[i])
            : values;
          if (!list.length) return;
          copyText(list.join(', '));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        };

        const selectAll = () => {
          setSelected(values.map((_, i) => i));
          anchorRef.current = 0;
        };

        const rangeSelect = (a0, b0) => {
          const [a, b] = [a0, b0].sort((x, y) => x - y);
          const range = [];
          for (let k = a; k <= b; k += 1) range.push(k);
          setSelected(range);
        };

        // mousedown on a chip: start a selection (and a potential drag-select)
        const startSelect = (i, e) => {
          if (e.button !== 0) return; // left button only
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey && anchorRef.current != null) {
            rangeSelect(anchorRef.current, i);
          } else if (e.ctrlKey || e.metaKey) {
            setSelected((prev) =>
              prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
            );
            anchorRef.current = i;
          } else {
            setSelected([i]);
            anchorRef.current = i;
            draggingRef.current = true; // begin drag-select
          }
          inputRef.current?.focus();
        };

        // dragging the mouse over chips extends the selection
        const extendSelect = (i) => {
          if (!draggingRef.current || anchorRef.current == null) return;
          rangeSelect(anchorRef.current, i);
        };

        const moveCaretTo = (idx) => {
          setInsertIndex(idx >= values.length ? null : idx);
          clearSelection();
          inputRef.current?.focus();
        };

        const handleKeyDown = (e) => {
          const mod = e.ctrlKey || e.metaKey;
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(inputValue);
          } else if ((e.key === 'a' || e.key === 'A') && mod && inputValue === '') {
            e.preventDefault();
            selectAll();
          } else if ((e.key === 'c' || e.key === 'C') && mod && inputValue === '') {
            e.preventDefault();
            handleCopy();
          } else if (e.key === 'Backspace' && inputValue === '') {
            if (selected.length) {
              e.preventDefault();
              deleteSelected();
            } else if (insertIdx > 0) {
              e.preventDefault();
              deleteAt(insertIdx - 1);
            }
          } else if (e.key === 'Delete' && inputValue === '') {
            // forward delete: remove the number right after the caret
            if (selected.length) {
              e.preventDefault();
              deleteSelected();
            } else if (insertIdx < values.length) {
              e.preventDefault();
              deleteAt(insertIdx);
            }
          } else if (e.key === 'ArrowLeft' && inputValue === '' && insertIdx > 0) {
            e.preventDefault();
            moveCaretTo(insertIdx - 1);
          } else if (e.key === 'ArrowRight' && inputValue === '' && insertIdx < values.length) {
            e.preventDefault();
            moveCaretTo(insertIdx + 1);
          } else if (e.key === 'Escape') {
            clearSelection();
            setInsertIndex(null);
          }
        };

        // Thin click target between chips that moves the caret (and the input)
        // there. A faint grey line shows on hover only.
        const gapEl = (idx) => (
          <Box
            key={`gap-${idx}`}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              moveCaretTo(idx);
            }}
            sx={{
              width: 8,
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'text',
              flexShrink: 0,
              '&:hover .gap-line': { opacity: 0.5 },
              '& .gap-line': {
                width: 2,
                height: 20,
                borderRadius: 1,
                bgcolor: 'text.disabled',
                opacity: 0,
                transition: 'opacity 0.12s ease',
              },
            }}
          >
            <Box className="gap-line" />
          </Box>
        );

        const chipEl = (val, i) => {
          const isSelected = selected.includes(i);
          return (
            <Chip
              key={`chip-${i}`}
              label={val}
              size="small"
              variant={isSelected ? 'filled' : 'soft'}
              color="primary"
              onMouseDown={(e) => startSelect(i, e)}
              onMouseEnter={() => extendSelect(i)}
              onDelete={() => deleteAt(i)}
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            />
          );
        };

        // Chips before the caret go in startAdornment, chips after go in
        // endAdornment. With `display: contents` they become inline flex
        // siblings of the real <input>, so the input physically sits at the
        // caret position between the two chip groups.
        const beforeNodes = [];
        for (let k = 0; k < insertIdx; k += 1) {
          beforeNodes.push(gapEl(k));
          beforeNodes.push(chipEl(values[k], k));
        }

        const afterNodes = [];
        for (let k = insertIdx; k <= values.length; k += 1) {
          if (k > insertIdx) afterNodes.push(gapEl(k));
          if (k < values.length) afterNodes.push(chipEl(values[k], k));
        }

        // Growing filler at the end of the row: clicking the empty trailing
        // area moves the caret to the end so you can always append there.
        const fillerEl = (
          <Box
            key="filler"
            onMouseDown={(e) => {
              e.preventDefault();
              moveCaretTo(values.length);
            }}
            sx={{ flex: '1 1 40px', minWidth: 40, alignSelf: 'stretch', cursor: 'text' }}
          />
        );
        afterNodes.push(fillerEl);

        const inputWidth =
          values.length === 0 && !inputValue
            ? 170
            : `${Math.max(inputValue.length, 2) + 1}ch`;

        return (
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              inputRef={inputRef}
              label={label}
              value={inputValue}
              error={isError}
              helperText={isError ? errorMessage : helperText}
              placeholder={values.length === 0 ? 'Type or paste: 1, 2, 3 …' : ''}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                setFocused(false);
                if (inputValue) commit(inputValue);
              }}
              onMouseDown={(e) => {
                // click on the empty area -> caret to end
                if (e.target === e.currentTarget) {
                  setInsertIndex(null);
                  clearSelection();
                }
              }}
              slotProps={{
                input: {
                  notched: shrink,
                  startAdornment:
                    beforeNodes.length > 0 ? (
                      <Box sx={{ display: 'contents' }}>{beforeNodes}</Box>
                    ) : null,
                  endAdornment:
                    afterNodes.length > 0 ? (
                      <Box sx={{ display: 'contents' }}>{afterNodes}</Box>
                    ) : null,
                  sx: {
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    columnGap: 0,
                    rowGap: '6px',
                    padding: '8px 44px 8px 12px',
                    '& .MuiInputBase-input': {
                      flex: '0 0 auto',
                      width: inputWidth,
                      minWidth: 50,
                      height: 24,
                      p: 0,
                      mx: '4px',
                    },
                  },
                },

                inputLabel: { shrink }
              }} />
            {values.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 8,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip
                  title={
                    copied
                      ? 'Copied!'
                      : selected.length
                        ? `Copy ${selected.length} selected`
                        : 'Copy all as "1, 2, 3"'
                  }
                >
                  <IconButton
                    size="small"
                    color={copied ? 'success' : 'default'}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <CheckIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
}
